"use client";

import { useEffect, useRef, useState } from "react";
import { ProductShowcase } from "@/components/home/product-showcase";
import { useLocalStorage } from "@/hooks/use-local-storage";
import type { Product } from "@/types";

const EMPTY_PRODUCTS: Product[] = [];
const EMPTY_STRING_ARRAY: string[] = [];

export function ContinueShoppingClient({ products = EMPTY_PRODUCTS }: { products?: Product[] }) {
  const [recent, , hydrated] = useLocalStorage<string[]>("mova-continue-shopping", EMPTY_STRING_ARRAY);
  const [matchedProducts, setMatchedProducts] = useState<Product[]>([]);
  const fetchedSlugsRef = useRef<string>("");

  useEffect(() => {
    if (!hydrated || !recent || recent.length === 0) return;

    if (products.length > 0) {
      const matched = recent
        .map((slug) => products.find((p) => p.slug === slug))
        .filter((p): p is Product => Boolean(p))
        .slice(0, 5);
      if (matched.length > 0) {
        setMatchedProducts(matched);
        return;
      }
    }

    const targetSlugs = recent.slice(0, 5).join(",");
    if (!targetSlugs || fetchedSlugsRef.current === targetSlugs) return;
    fetchedSlugsRef.current = targetSlugs;

    let active = true;
    fetch(`/api/products?slugs=${encodeURIComponent(targetSlugs)}`)
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (active && data?.items) setMatchedProducts(data.items);
      })
      .catch(() => {});

    return () => {
      active = false;
    };
  }, [hydrated, recent, products]);

  if (!hydrated || matchedProducts.length === 0) return null;

  return (
    <ProductShowcase
      title="Continue shopping"
      subtitle="Items you recently viewed."
      products={matchedProducts}
      href="/search"
    />
  );
}
